// Reactive feature flag system - no page reloads required
// Syncs across tabs via storage events

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from '../api/axiosConfig';

const FeatureFlagContext = createContext({
  get: () => false,
  set: () => {},
  getAll: () => ({}),
  reset: () => {},
  resetAll: () => {}
});

// Feature flag definitions
const FLAG_DEFINITIONS = {
  'density.optimized': {
    key: 'bookmarks.density.optimized',
    default: false,
    description: 'Optimized row density (56px rows, 8px padding)'
  },
  'favicons.ssr': {
    key: 'bookmarks.favicons.ssr', 
    default: false,
    description: 'Use server-side favicon service'
  },
  'metrics.enabled': {
    key: 'bookmarks.metrics.enabled',
    default: false,
    description: 'Enable UX metrics collection'
  },
  'grouped.default': {
    key: 'bookmarks.grouped.default',
    default: false,
    description: 'Default to grouped project view'
  },
  'pagination.v2': {
    key: 'bookmarks.pagination.v2',
    default: false,
    description: 'Enable cursor-based pagination'
  },
  'PROJECTS_DBLCLICK': {
    key: 'sessions.projects.dblclick',
    default: false,
    description: 'Enable double-click navigation for projects'
  },
  'FAVICONS_SSR': {
    key: 'favicons.ssr',
    default: false,
    description: 'Use server-side favicon proxy instead of client-side fetching'
  },
  'DEBUG_MODE': {
    key: 'debug.console.enabled',
    default: false,
    description: 'Enable console logging for debugging'
  },
  'nav.v2': {
    key: 'nav.v2',
    default: false,
    description: 'New navigation order and labels'
  },
  'bookmarks.header.v2': {
    key: 'bookmarks.header.v2',
    default: false,
    description: 'Simplified bookmarks header redesign'
  },
  
  // Phase 0 - Hygiene & Setup flags
  'notes.composer.voiceOff': {
    key: 'notes.composer.voiceOff',
    default: true, // Default to true to hide voice UI
    description: 'Hide voice controls in Notes editor'
  },
  'notes.ui.v2': {
    key: 'notes.ui.v2',
    default: true, // Re-enable to test dashboard with Notes UI v2
    description: 'Enable Notes UI v2 improvements'
  },
  'notes.list.compact.v1': {
    key: 'notes.list.compact.v1',
    default: false,
    description: 'Compact list view for Notes'
  },
  'notes.links.v1': {
    key: 'notes.links.v1',
    default: false,
    description: 'Enable note linking features'
  },
  'notes.graph.mini.v1': {
    key: 'notes.graph.mini.v1',
    default: false,
    description: 'Mini graph visualization for note connections'
  },
  'ai.title.auto.notes.v1': {
    key: 'ai.title.auto.notes.v1',
    default: true, // Enable for testing
    description: 'Auto-generate titles for notes using AI'
  },
  'notes.stickyActions.v1': {
    key: 'notes.stickyActions.v1',
    default: true, // TEMPORARY: Enabled for local testing
    description: 'Enable sticky top bar with auto-title and autosave for notes editor',
    category: 'notes'
  },
  'ai.title.auto.voices.v1': {
    key: 'ai.title.auto.voices.v1',
    default: true, // Enable for testing
    description: 'Auto-generate titles for voice notes using AI'
  },
  'ai.tags.auto.v1': {
    key: 'ai.tags.auto.v1',
    default: false,
    description: 'Auto-generate tags using AI'
  },
  'ai.tags.personalize.v1': {
    key: 'ai.tags.personalize.v1',
    default: false,
    description: 'Personalized tag suggestions based on user patterns'
  },
  'project.selector.v2': {
    key: 'project.selector.v2',
    default: false,
    description: 'Unified project search and switcher'
  },
  'insights.v1': {
    key: 'insights.v1',
    default: false,
    description: 'Insights tab MVP with basic tiles'
  },
  'cmdbar.v1': {
    key: 'cmdbar.v1',
    default: false,
    description: 'Command bar (Cmd/Ctrl+K) with scoped search and project switching'
  },
  'nav.order.v2': {
    key: 'nav.order.v2',
    default: false,
    description: 'Reordered navigation with renamed labels'
  },
  'projects.cards.compact.v1': {
    key: 'projects.cards.compact.v1',
    default: true,
    description: 'Compact card view for projects/sessions'
  },
  'PROJECTS_INLINE_RENAME': {
    key: 'projects.inline.rename',
    default: false,
    description: 'Enable inline renaming of projects'
  },
  
  // Bookmark sync features
  'bookmarks.realtime.sync': {
    key: 'bookmarks.realtime.sync',
    default: false,
    description: 'Enable realtime bookmark synchronization between extension and web app'
  },

  // Notes editor enhancements
  'notes.markdown.v1': {
    key: 'notes.markdown.v1',
    default: false,
    description: 'Enable Markdown mode toggle in Notes editor',
    category: 'notes'
  }
};

// Read production overrides from localStorage
const readProductionOverrides = () => {
  try {
    const overrides = localStorage.getItem('ff:overrides');
    return overrides ? JSON.parse(overrides) : {};
  } catch (error) {
    return {};
  }
};


// Read all flags from localStorage (dev mode)
const readFromLocalStorage = () => {
  const flags = {};

  Object.entries(FLAG_DEFINITIONS).forEach(([flagName, definition]) => {
    const stored = localStorage.getItem(definition.key);

    // OVERRIDE: Force notes.stickyActions.v1 to true for testing (ignore localStorage)
    if (flagName === 'notes.stickyActions.v1') {
      flags[flagName] = true;
      localStorage.setItem(definition.key, 'true'); // Update localStorage to match
    } else if (stored !== null) {
      flags[flagName] = stored === 'true';
    } else {
      flags[flagName] = definition.default;
    }
  });

  return flags;
};

// Fetch defaults from server (public endpoint)
const fetchServerDefaults = async () => {
  try {
    const response = await axios.get('/api/feature-flags/defaults');
    if (response.data.success) {
      return response.data.flags;
    }
  } catch (error) {
  }
  return null;
};

// Fetch user flags from server (authenticated endpoint)
const fetchUserFlags = async () => {
  try {
    const response = await axios.get('/api/feature-flags');
    if (response.data.success) {
      return response.data.flags;
    } else {
      return response.data.flags; // Still use the fallback
    }
  } catch (error) {
    return null; // Use defaults
  }
};

// Feature Flag Provider Component
export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState(() => {
    // In dev, use localStorage. In prod, use defaults + overrides
    const base = process.env.NODE_ENV === 'production' 
      ? Object.fromEntries(Object.entries(FLAG_DEFINITIONS).map(([key, def]) => [key, def.default]))
      : readFromLocalStorage();
    
    const overrides = readProductionOverrides();
    return { ...base, ...overrides };
  });
  const [serverHydrated, setServerHydrated] = useState(false);

  // Listen for cross-tab synchronization and manual updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Re-read all flags on any flag-related storage change
      if (e.key === 'ff:overrides' || Object.values(FLAG_DEFINITIONS).some(def => def.key === e.key)) {
        const base = process.env.NODE_ENV === 'production' 
          ? Object.fromEntries(Object.entries(FLAG_DEFINITIONS).map(([key, def]) => [key, def.default]))
          : readFromLocalStorage();
        
        const overrides = readProductionOverrides();
        setFlags({ ...base, ...overrides });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Skip server hydration in dev mode for faster iteration
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setServerHydrated(true);
    } else {
      const hydrateFromServer = async () => {
        let serverFlags = await fetchServerDefaults();
        
        if (axios.defaults.headers.common['Authorization']) {
          const userFlags = await fetchUserFlags();
          if (userFlags) serverFlags = userFlags;
        }
        
        if (serverFlags) {
          const localFlags = readFromLocalStorage();
          const overrides = readProductionOverrides();
          const merged = { ...serverFlags, ...localFlags, ...overrides };
          setFlags(merged);
        }
        setServerHydrated(true);
      };
      hydrateFromServer();
    }
  }, []);

  const api = useMemo(() => ({
    get: (key) => {
      if (!(key in FLAG_DEFINITIONS)) {
        return false;
      }
      return !!flags[key];
    },

    set: (key, value) => {
      if (!(key in FLAG_DEFINITIONS)) {
        return;
      }

      const definition = FLAG_DEFINITIONS[key];
      const boolValue = !!value;
      
      // Persist to localStorage first
      try {
        localStorage.setItem(definition.key, String(boolValue));
      } catch (e) {
      }
      
      // Update React state (triggers immediate re-render)
      setFlags(currentFlags => ({
        ...currentFlags,
        [key]: boolValue
      }));

      if (process.env.NODE_ENV !== 'production') {
      }
    },

    getAll: () => ({ ...flags }),
    
    // Internal method for production overrides to trigger re-render
    _setFlags: (newFlags) => {
      setFlags(newFlags);
    },

    reset: (key) => {
      if (!(key in FLAG_DEFINITIONS)) {
        return;
      }

      const definition = FLAG_DEFINITIONS[key];
      localStorage.removeItem(definition.key);
      
      setFlags(currentFlags => ({
        ...currentFlags,
        [key]: definition.default
      }));
    },

    resetAll: () => {
      Object.keys(FLAG_DEFINITIONS).forEach(key => {
        const definition = FLAG_DEFINITIONS[key];
        localStorage.removeItem(definition.key);
      });
      
      setFlags(readFromLocalStorage());
    }

  }), [flags]);

  // Expose window.featureFlags API for real-time dev use
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.featureFlags = {
        get: api.get,
        set: api.set,
        getAll: api.getAll,
        reset: api.reset,
        resetAll: api.resetAll
      };
    }
  }, [api]);

  return (
    <FeatureFlagContext.Provider value={api}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Hook for consuming feature flags
export const useFeatureFlag = (key) => {
  const context = useContext(FeatureFlagContext);
  
  if (!context) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
  }

  return [
    context.get(key),
    (value) => context.set(key, value)
  ];
};

// Hook for accessing all feature flags
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }

  return context;
};